import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import EvaluationModal from "../components/EvaluationModal";
import { useAuth } from "../contexts/AuthContext";
import LoadingScreen from "../components/LoadingScreen";

const API_BASE = process.env.REACT_APP_API_BASE || "/.netlify/functions/api";

const rmSpacesDash = (s = "") =>
  s.toString().trim().toLowerCase().replace(/\s+/g, " ").replace(/-/g, " ");
const stripArabic = (s = "") =>
  s
    .replace(/[\u0617-\u061A\u064B-\u0652\u0670]/g, "")
    .replace(/\u0640/g, "")
    .replace(/[^\u0600-\u06FF0-9a-zA-Z\s]/g, " ");
const arVariants = (s = "") =>
  s
    .replace(/[إأآا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(/ة/g, "ه");
const norm = (s = "") => arVariants(stripArabic(rmSpacesDash(s))).replace(/\s+/g, "");
const keyNorm = (s = "") => norm(s);
const looksLikeShortCode = (s = "") => /^[a-z0-9]{6,12}$/i.test(String(s));

const djb2 = (str) => {
  let h = 5381 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  }
  return h >>> 0;
};

const shortCodeFromName = (name = "") => {
  const k = keyNorm(name || "");
  const u32 = djb2(k);
  const b36 = u32.toString(36).toLowerCase();
  return (b36 + "00000000").slice(0, 8);
};

const nameFromAnyId = (id = "") => {
  if (!id) return "";
  let x = String(id);
  try {
    x = decodeURIComponent(x);
  } catch {}
  x = x.replace(/^\/+|\/+$/g, "");
  x = x.replace(/^(doctor-reviews|course-reviews)\//i, "");
  return x.replace(/-/g, " ").trim();
};

const isLocalHost = (h = "") =>
  ["localhost", "127.0.0.1", "::1"].includes(h) || /\blocal\b/i.test(h);

const hostHint = () => {
  if (typeof window === "undefined") return "";
  const h = window.location?.hostname || "";
  if (!h || isLocalHost(h)) return "";
  const parts = h.split(".");
  if (parts.length < 2) return "";
  const sub = parts[0];
  if (!sub || sub.toLowerCase() === "www") return "";
  try {
    return decodeURIComponent(sub.replace(/-/g, " "));
  } catch {
    return sub.replace(/-/g, " ");
  }
};

const pathHint = () => {
  if (typeof window === "undefined") return "";
  const p = window.location?.pathname || "";
  const segs = p.split("/").filter(Boolean);
  const last = segs[segs.length - 1] || "";
  if (!last) return "";
  if (/(lecturer|course|doctor-reviews|course-reviews|reviews|details|evaluation)/i.test(last))
    return "";
  try {
    return decodeURIComponent(last.replace(/-/g, " "));
  } catch {
    return last.replace(/-/g, " ");
  }
};

const PALETTE = {
  good: "rgba(95, 204, 156, 1)",
  warn: "rgba(241, 194, 103, 1)",
  bad: "rgba(250, 134, 134, 1)",
  danger: "rgba(180, 35, 24, 1)",
};

const toPct = (v) => {
  let n = Number(v);
  if (Number.isNaN(n)) n = 0;
  if (n <= 1) n = n * 100;
  if (n < 0) n = 0;
  if (n > 100) n = 100;
  return Math.round(n);
};

const getRatingColor = (rating = 0) => {
  const r = toPct(rating);
  if (r >= 80) return PALETTE.good;
  if (r >= 60) return PALETTE.warn;
  if (r >= 30) return PALETTE.bad;
  return PALETTE.danger;
};

const withAlpha = (color, alpha = 0.12) => {
  if (!color) return `rgba(0,0,0,${alpha})`;
  const m = String(color).match(/^rgba?\(([^)]+)\)$/i);
  if (m) {
    const parts = m[1].split(",").map((x) => x.trim());
    const [r, g, b] = parts.map((v, i) => (i < 3 ? Number(v) : v));
    return `rgba(${r || 0}, ${g || 0}, ${b || 0}, ${alpha})`;
  }
  let hex = color.replace("#", "").trim();
  if (hex.length === 3) hex = hex.split("").map((ch) => ch + ch).join("");
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16) || 0;
    const g = parseInt(hex.slice(2, 4), 16) || 0;
    const b = parseInt(hex.slice(4, 6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
};

const BUCKET_LISTS = {
  good: [
    "يشرح بشكل مبسط",
    "شرح بأمثلة واقعية تقرب الفكرة",
    "يسهل لك المادة",
    "من أفضل الشروحات التي مرت علي",
    "أسلوبه ممتع وشيق",
    "كويزات سهلة وواضحة",
    "تكاليف بسيطة وتعليمية",
    "يتعاون في التصحيح",
    "يساعد بيونس محفز",
    "ما يقصر أو يظلم أحد",
    "محاضرات منظمة ومنهجية",
    "محتوى محدث ومفيد",
    "أمثلة عملية وتطبيقية",
    "مصادر إضافية مفيدة",
    "تحضير جيد للمحاضرات",
    "صبور في الشرح والنقاش",
    "مرح و يبغى يكسبكم",
    "يرد بسرعة على الرسائل",
    "يشجع و يسمع للطلاب ويتفهمهم",
    "محترم جدا ويتعامل بذوق",
    "من قائمة الأفضل في الجامعة",
    "الكل يمدحه",
    "الله يسعده ويكثر من أمثاله",
    "أسطورة بكل معنى الكلمة",
    "تسجل عنده وانت مغمض",
    "في الجيب +A",
    "سهلة و ممتعة",
    "من امتع المواد اللي درستها",
    "واضحه و ما تحتاج خصوصي",
    "تلحق تذاكرها قبل بأقل من اسبوع",
    "السلايدات سهله و الاختبارت لك عليها",
    "مشروعها سهل",
    "ممتعه و انت تذاكرها",
    "ماده مهمه في مجالنا",
    "تكاليفها بسيطة و مهمه دراسيا",
    "اختباراتها سهله و واضحه",
    "تقدر تعديها باعمال السنه",
  ],
  warn: [
    "شرحه واضح أحيانا وأحيانا لا",
    "يعتمد كثير على السلايدات",
    "تقدر تفهم إذا ركزت",
    "يشرح باختصار لكن يجاوب على الأسئلة",
    "شرحه عادي يمشي الحال",
    "لازم تذاكر من أكثر من مصدر",
    "يطلب تكاليف شوي كثيرة",
    "حقاني في الدرجات",
    "كويزات شويه صعبة",
    "ممكن يظلم في التصحيح",
    "مادة علمية حديثة",
    "أبحاث ودراسات حديثة",
    "محتوى متوازن",
    "أمثلة من الحياة العملية",
    "مصادر متنوعة",
    "يعطي وجه بس ما يساعد",
    "يسمع بس ما يغير رأيه",
    "ما يضحك كثير بس محترم",
    "يرد على الرسائل متأخر",
    "طلباته فوق طاقتك",
    "كويس بس مو مميز",
    "يمشي الحال",
    "فيه ناس تمدحه وناس لا",
    "يعتمد على مزاجه",
    "جيد أكاديميا بس ممل شوي",
    "نص و نص",
    "على حسب دكتورك في المادة",
    "لازم لك مصادر شرح لها",
    "يبغالك تذاكر قبل ب ١٠-١٤ ايام",
    "اختار الدكتور قبل تختار الماده",
    "تقدر تعديها بس شد حيلك لازم",
    "يبغالك اول باول / قبل باكثر من اسبوعين",
    "كويزات شويه صعبة",
    "تكاليف شوي كثيرة",
    "كلها نظري و كلام",
    "عديها وبس",
    "اختار دكتورك قبل الماده",
    "يبغالك اول باول",
  ],
  bad: [
    "يقرأ من السلايد فقط",
    "ما يعطي أمثلة كفاية",
    "شرحه ممل شوي",
    "تعتمد على نفسك أكثر من شرحه",
    "ما يهتم إذا فهمنا أو لا",
    "اختباراته غريبة وما تتكرر",
    "يصحح بدقة زائدة",
    "يعطي درجه نص حتى لو غلط بسيط",
    "يكثر أسئلة بين السطور",
    "تكاليفه كثيرة بدون هدف واضح",
    "يدقق على التفاهات",
    "تعامله يختلف من طالب لطالب",
    "يجاوب بجفاف",
    "يحسس الطلاب إنهم يزعجونه",
    "أحيانا يعصب بدون سبب",
    "مو مرة أنصح فيه",
    "أغلب الطلاب ما ارتاحوا",
    "المادة معه أثقل من غيره",
    "تعامله يخليك تكره الماده",
    "فيه ظلم أحيانا",
    "شوي معقدة",
    "مصادرها مشتته",
    "تخلص مذاكرتها و ما تدري ايش ذاكرت",
    "المادة سهله بس الاختبارات صعبه جدا",
  ],
  danger: [
    "اسوا تجربة",
    "شرحه مشتت وغير مفهوم",
    "محاضرة هواجيس",
    "صعب جدا تتابعه أو تفهم منه",
    "يشرح بمصطلحات غير مفهومة",
    "شرح بريال",
    "مستحيل تجيب درجة كاملة",
    "أسئلته تعجيزية",
    "ظلم في التصحيح",
    "يعطى أقل من المتوقع دائما",
    "طلباته فوق طاقتك",
    "ما يستمع للطلاب",
    "يصعب التواصل معه تماما",
    "نرجسي مع الطلاب",
    "يتكلم بطريقة غير لائقة",
    "تعامل سيء جدا",
    "اسوء تجربة",
    "المادة سهله معه صعبه",
    "لا يشرح ولا يتجاوب",
    "أغلب الطلاب يحذرون منه",
    "ما يستمع للطلاب",
    "مافيها شي واضح",
    "الدكتور نفسه مو فاهم المنهج",
    "مالها مصدر معين",
    "تذاكر سنه قبل و بعد صعبه",
    "منسقها مريض",
    "مالها مصادر",
    "أسئلته تعجيزية و معقده",
    "المادة صعبه جدا",
  ],
};

const bucketFromText = (txt = "") => {
  const k = norm(txt);
  for (const b of ["good", "warn", "bad", "danger"]) {
    if (BUCKET_LISTS[b].some((t) => norm(t) === k)) return b;
  }
  for (const b of ["good", "warn", "bad", "danger"]) {
    if (BUCKET_LISTS[b].some((t) => k.includes(norm(t)) || norm(t).includes(k))) return b;
  }
  return null;
};

const getClassificationColors = (c) => {
  switch (c) {
    case "A+":
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)",
      };
    case "A":
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(34, 209, 57, 0.5) 0%, rgba(34, 209, 57, 0) 100%), rgba(34, 209, 57, 0.25)",
      };
    case "B+":
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(60, 209, 34, 0.5) 0%, rgba(60, 209, 34, 0) 100%), rgba(60, 209, 34, 0.44)",
      };
    case "B":
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(60, 209, 34, 0.5) 0%, rgba(60, 209, 34, 0) 100%), rgba(104, 209, 34, 0.57)",
      };
    case "C+":
      return {
        background:
          "radial-gradient(43.64% 77.64% at 50% -10.42%, rgba(208, 227, 40, 0.5) 0%, rgba(208, 227, 40, 0) 100%), rgba(207, 248, 20, 0.55)",
      };
    case "C":
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 218, 40, 0.5) 0%, rgba(227, 218, 40, 0) 100%), rgba(227, 218, 40, 0.49)",
      };
    case "D+":
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 171, 40, 0.5) 0%, rgba(227, 171, 40, 0) 100%), rgba(227, 124, 40, 0.5)",
      };
    case "D":
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 71, 40, 0.5) 0%, rgba(227, 71, 40, 0) 100%), rgba(227, 71, 40, 0.36)",
      };
    case "F":
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(227, 40, 90, 0.5) 0%, rgba(227, 40, 59, 0) 100%), rgba(227, 40, 90, 0.25)",
      };
    default:
      return {
        background:
          "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)",
      };
  }
};

const tagVisualFromNameOrSentiment = (text = "", sentiment) => {
  let bucket = null;
  if (sentiment) {
    const s = String(sentiment).toLowerCase();
    if (s === "positive") bucket = "good";
    else if (s === "negative") bucket = "danger";
    else bucket = "warn";
  } else {
    bucket = bucketFromText(text);
  }

  if (!bucket) {
    return {
      active: false,
      border: "#2a3a4a",
      background:
        "linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%), #081423",
      dot: "#2a3a4a",
      shadow: "transparent",
    };
  }

  const borderMap = {
    good: "#2A9E8D",
    warn: "#F1C267",
    bad: "#FA8686",
    danger: "#B42318",
  };
  return {
    active: true,
    border: borderMap[bucket],
    background:
      "linear-gradient(0deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0.02)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(40, 227, 218, 0.2) 0%, rgba(40, 227, 218, 0) 100%), #081423",
    dot: PALETTE[bucket],
    shadow: "rgba(162, 233, 189, 0.6)",
  };
};

const getTagText = (tag) => {
  if (!tag) return "";
  if (typeof tag === "string") return tag.trim();
  if (typeof tag === "object") {
    return (
      tag.text ||
      tag.name ||
      tag.label ||
      tag.tag ||
      (typeof tag.value === "string" ? tag.value : "")
    );
  }
  return String(tag);
};

const getReviewCourseCodes = (ev = {}) => {
  const out = [];
  if (ev.courseCode) out.push(ev.courseCode);
  if (Array.isArray(ev.courseCodes)) out.push(...ev.courseCodes);
  if (ev.course_code) out.push(ev.course_code);
  if (Array.isArray(ev.course_codes)) out.push(...ev.course_codes);
  if (ev.code) out.push(ev.code);
  return out
    .map((c) => String(c || "").trim())
    .filter((c) => c.length > 0);
};

const filterCourseReviews = (arr = [], doctor, entityQuery = "") => {
  if (!Array.isArray(arr) || arr.length === 0) return [];

  const targets = new Set();
  if (doctor?.code) targets.add(norm(doctor.code));
  if (doctor?.name) targets.add(norm(doctor.name));
  if (entityQuery) targets.add(norm(entityQuery));

  const hasTarget = targets.size > 0;

  return arr.filter((ev) => {
    if (ev.targetType && ev.targetType !== "course") return false;

    const codes = getReviewCourseCodes(ev);
    const names = [
      ev.course,
      ev.courseName,
      ev.title,
      ev.course_title,
      ev.course_title_ar,
    ]
      .filter(Boolean)
      .map((s) => norm(String(s)));

    const strings = [...codes.map(norm), ...names];

    if (!hasTarget) return true;

    for (const t of targets) {
      if (strings.some((s) => s === t || s.includes(t) || t.includes(s))) return true;
    }
    return false;
  });
};

const collectCourseCodes = (arr = [], doctor) => {
  const set = new Set();
  if (!Array.isArray(arr)) return [];
  arr.forEach((ev) => {
    getReviewCourseCodes(ev).forEach((c) => set.add(c));
  });
  if (doctor?.code) set.add(String(doctor.code));
  return Array.from(set);
};

function buildSummaryQuery({ focusType, doctorName, courseName }) {
  const params = new URLSearchParams();
  if (focusType === "doctor" && doctorName) {
    params.set("doctorname", doctorName);
    if (courseName) params.set("coursename", courseName);
  } else if (courseName) {
    params.set("coursename", courseName);
  }
  return params.toString();
}

function buildReviewsQuery({ focusType, doctorName, courseName }) {
  const params = new URLSearchParams();
  if (focusType === "doctor" && doctorName) {
    params.set("doctorname", doctorName);
    if (courseName) params.set("coursename", courseName);
  } else if (courseName) {
    params.set("coursename", courseName);
  }
  return params.toString();
}

const pickStr = (...vals) => {
  for (const v of vals) {
    if (v === null || v === undefined) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
};

const arrFromAny = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  return [v];
};

const uniqClean = (arr) =>
  Array.from(
    new Set(
      (arr || [])
        .flatMap((x) => arrFromAny(x))
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    )
  );

const getCourseNameRow = (c) => pickStr(c?.name, c?.Name);
const getCourseCodeRow = (c) => pickStr(c?.code, c?.Code, c?.course_code, c?.courseCode);
const getPrereqNameRow = (c) =>
  pickStr(
    c?.prerequisite_name,
    c?.prerequisiteName,
    c?.prerequisite,
    c?.Prerequisite,
    c?.pre_name
  );
const getPrereqCodeRow = (c) =>
  pickStr(
    c?.prerequisite_code,
    c?.prerequisiteCode,
    c?.prerequisite_code_text,
    c?.pre_code
  );
const getPlanNoRow = (c) => {
  const v = c?.plan_no ?? c?.planNo ?? c?.plan ?? c?.batch ?? c?.Batch ?? c?.PLAN_NO ?? null;
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
};

const EvaluationDetailsPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();

  const urlType = useMemo(() => {
    if (params.type === "course" || params.type === "lecturer") return params.type;
    if (typeof window !== "undefined") {
      const p = window.location?.pathname || "";
      if (/course-reviews/i.test(p)) return "course";
      if (/doctor-reviews|lecturer-reviews/i.test(p)) return "lecturer";
    }
    return "lecturer";
  }, [params.type]);

  const type = urlType;
  const rawIdParam =
    params.id || params.code || params.slug || params.shortCode || params.reviewId || "";

  const [sortBy, setSortBy] = useState("mostLiked");
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);

  const [doctor, setDoctor] = useState(null);
  const [directoryLoading, setDirectoryLoading] = useState(true);

  const [summary, setSummary] = useState({ overallRating: 0, categories: [], tags: [] });
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const [animatedPercentages, setAnimatedPercentages] = useState({});
  const [reviewAnimatedPercentages, setReviewAnimatedPercentages] = useState({});

  const [courseCodes, setCourseCodes] = useState([]);

  const [dbCourseCodes, setDbCourseCodes] = useState([]);
  const [dbPrereqCodes, setDbPrereqCodes] = useState([]);
  const [dbPrereqNames, setDbPrereqNames] = useState([]);
  const [dbPlanNos, setDbPlanNos] = useState([]);

  const [myChoices, setMyChoices] = useState({});

  const reviewId = searchParams.get("review");
  const queryName = searchParams.get("name") || searchParams.get("n") || searchParams.get("q") || "";
  const rawId = rawIdParam || "";

  const guessedName = useMemo(() => (queryName ? queryName : nameFromAnyId(rawId)), [rawId, queryName]);
  const guessedKey = useMemo(() => norm(guessedName), [guessedName]);

  const hostGuess = useMemo(() => hostHint(), []);
  const pathGuess = useMemo(() => pathHint(), []);

  const entityQuery = useMemo(() => {
    return doctor?.name || rawId || hostGuess || pathGuess || guessedName || "";
  }, [doctor?.name, rawId, hostGuess, pathGuess, guessedName]);

  const openHeaderLoginModal = () => {
    try {
      window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { mode: "login" } }));
    } catch {}
  };

  async function tryResolveLecturer(any) {
    if (!any) return null;
    try {
      const r = await fetch(`${API_BASE}/directory/resolve?id=${encodeURIComponent(any)}`);
      if (!r.ok) return null;
      const d = await r.json();
      return {
        id: d.id || d._id || d.slug || d.code || d.shortCode || "",
        name: d.name || "",
        college: d.college || "",
        Department: d.Department || "",
        specialization: d.specialty || d.specialization || "",
        email: d.Email || d.email || "",
      };
    } catch {
      return null;
    }
  }

  useEffect(() => {
    if (type !== "lecturer") return;

    let cancelled = false;
    (async () => {
      try {
        setDirectoryLoading(true);
        let hit = null;

        if (looksLikeShortCode(rawId)) hit = await tryResolveLecturer(rawId);
        if (!hit) hit = await tryResolveLecturer(guessedName);
        if (!hit && hostGuess) hit = await tryResolveLecturer(hostGuess);
        if (!hit && pathGuess) hit = await tryResolveLecturer(pathGuess);

        if (!hit) {
          const q = guessedName || rawId || hostGuess || pathGuess || "";
          if (q) {
            const rs = await fetch(`${API_BASE}/directory/search?q=${encodeURIComponent(q)}`);
            if (rs.ok) {
              const arr = (await rs.json()) || [];
              const k = guessedKey;
              const m =
                (k && arr.find((d) => norm(d?.name || "") === k)) ||
                (k && arr.find((d) => norm(d?.name || "").includes(k) || k.includes(norm(d?.name || "")))) ||
                arr[0] ||
                null;
              if (m) {
                hit = {
                  name: m.name || "",
                  college: m.college || "",
                  Department: m.Department || "",
                  specialization: m.specialty || m.specialization || "",
                  email: m.Email || m.email || "",
                };
              }
            }
          }
        }

        if (!hit) {
          const res = await fetch(`${API_BASE}/directory`);
          if (res.ok) {
            const list = (await res.json()) || [];
            const candidates = [guessedName, rawId, hostGuess, pathGuess].filter(Boolean);
            let m = null;
            for (const c of candidates) {
              const k = norm(c);
              m =
                (k && list.find((d) => norm(d?.name || "") === k)) ||
                (k && list.find((d) => norm(d?.name || "").includes(k) || k.includes(norm(d?.name || ""))));
              if (m) break;
            }
            if (m) {
              hit = {
                name: m.name || "",
                college: m.college || "",
                Department: m.Department || "",
                specialization: m.specialty || m.specialization || "",
                email: m.Email || m.email || "",
              };
            }
          }
        }

        if (!cancelled) setDoctor(hit || null);
      } catch {
        if (!cancelled) setDoctor(null);
      } finally {
        if (!cancelled) setDirectoryLoading(false);
      }
    })();
  }, [type, rawId, guessedName, guessedKey, hostGuess, pathGuess]);

  useEffect(() => {
    if (type !== "course") return;

    let cancelled = false;
    (async () => {
      try {
        setDirectoryLoading(true);

        const q = guessedName || rawId || hostGuess || pathGuess || "";
        const res = await fetch(`${API_BASE}/courses`);
        if (!res.ok) throw new Error("courses fail");
        const list = (await res.json()) || [];

        let hit = null;
        const idLower = (rawId || "").trim().toLowerCase();
        const qNorm = norm(q);

        if (looksLikeShortCode(rawId)) {
          hit =
            list.find((c) => {
              const nm = getCourseNameRow(c);
              const sc = shortCodeFromName(nm);
              return sc.toLowerCase() === idLower;
            }) || null;
        }

        if (!hit && qNorm) {
          hit =
            list.find((c) => norm(getCourseNameRow(c)) === qNorm) ||
            list.find((c) => {
              const n = norm(getCourseNameRow(c));
              return n.includes(qNorm) || qNorm.includes(n);
            }) ||
            null;
        }

        if (!cancelled) {
          if (hit) {
            const baseName = getCourseNameRow(hit);
            const baseKey = norm(baseName);

            const sameCourseRows = list.filter((c) => norm(getCourseNameRow(c)) === baseKey);

            const codes = uniqClean(sameCourseRows.map(getCourseCodeRow));
            const preCodes = uniqClean(sameCourseRows.map(getPrereqCodeRow));
            const preNames = uniqClean(sameCourseRows.map(getPrereqNameRow));
            const planNos = uniqClean(
              sameCourseRows
                .map(getPlanNoRow)
                .filter((x) => x !== null && x !== undefined)
                .map((x) => String(x))
            );

            setDbCourseCodes(codes);
            setDbPrereqCodes(preCodes);
            setDbPrereqNames(preNames);
            setDbPlanNos(planNos);

            setDoctor({
              id: hit.id || hit._id || null,
              name: baseName,
              college: pickStr(hit.college, hit.College, hit.college_name, hit.CollegeName),
              Department: pickStr(hit.department, hit.Department, hit.department_name, hit.DepartmentName),
              specialization: pickStr(hit.category, hit.type),
              email: "",
              code: codes[0] || getCourseCodeRow(hit) || "",
              codes,
              prerequisiteCodes: preCodes,
              prerequisiteNames: preNames,
              planNos,
            });
          } else {
            setDoctor(null);
            setDbCourseCodes([]);
            setDbPrereqCodes([]);
            setDbPrereqNames([]);
            setDbPlanNos([]);
          }
        }
      } catch {
        if (!cancelled) {
          setDoctor(null);
          setDbCourseCodes([]);
          setDbPrereqCodes([]);
          setDbPrereqNames([]);
          setDbPlanNos([]);
        }
      } finally {
        if (!cancelled) setDirectoryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [type, rawId, guessedName, hostGuess, pathGuess]);

  useEffect(() => {
    const q = (entityQuery || "").trim();
    const focusType = type === "course" ? "course" : "doctor";

    if (!q && !doctor?.name) {
      setSummary({ overallRating: 0, categories: [], tags: [] });
      setReviews([]);
      setMyChoices({});
      setCourseCodes([]);
      return;
    }

    let cancelled = false;
    const mainName = doctor?.name || q;

    (async () => {
      try {
        setSummaryLoading(true);

        const summaryQs = buildSummaryQuery({
          focusType,
          doctorName: focusType === "doctor" ? mainName : "",
          courseName: focusType === "course" ? mainName : "",
        });

        if (!summaryQs) {
          if (!cancelled) setSummary({ overallRating: 0, categories: [], tags: [] });
          return;
        }

        const r = await fetch(`${API_BASE}/reviews/summary?${summaryQs}`);
        if (!r.ok) throw new Error("summary fail");
        const data = (await r.json()) || {};

        if (!cancelled) {
          const directTags = [];
          const rawTags = [
            data.tags,
            data.topTags,
            data.keywords,
            data.explanationtags,
            data.testtags,
            data.gradetags,
            data.explanation_tags,
            data.test_tags,
            data.grade_tags,
          ];
          rawTags.forEach((arr) => {
            if (Array.isArray(arr)) directTags.push(...arr);
          });

          setSummary({
            overallRating: data.overallRating || 0,
            categories: Array.isArray(data.categories) ? data.categories : [],
            tags: directTags,
          });
        }
      } catch {
        if (!cancelled) setSummary({ overallRating: 0, categories: [], tags: [] });
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();

    (async () => {
      try {
        setReviewsLoading(true);

        const baseQs = buildReviewsQuery({
          focusType,
          doctorName: focusType === "doctor" ? mainName : "",
          courseName: focusType === "course" ? mainName : "",
        });

        if (!baseQs) {
          if (!cancelled) {
            setReviews([]);
            setMyChoices({});
            setCourseCodes([]);
          }
          return;
        }

        const qsWithSort = `${baseQs}&sortBy=${sortBy === "mostLiked" ? "mostliked" : "newest"}`;

        const commonHeaders = {
          Accept: "application/json",
          ...(user?.id ? { "x-user-id": user.id } : {}),
        };

        const r = await fetch(`${API_BASE}/reviews?${qsWithSort}`, {
          credentials: "include",
          headers: commonHeaders,
        });

        if (!r.ok) throw new Error("reviews fail");

        const list = (await r.json()) || [];

        if (!cancelled) {
          let arr = Array.isArray(list) ? list : [];

          if (focusType === "course") arr = filterCourseReviews(arr, doctor, q);

          setReviews(arr);

          const initChoices = {};
          arr.forEach((ev) => {
            if (ev?.id && ["agree", "neutral", "disagree"].includes(ev?.myChoice)) {
              initChoices[String(ev.id)] = ev.myChoice;
            }
          });
          setMyChoices(initChoices);

          if (focusType === "course") {
            setCourseCodes(collectCourseCodes(arr, doctor));
          } else {
            setCourseCodes([]);
          }
        }
      } catch {
        if (!cancelled) {
          setReviews([]);
          setMyChoices({});
          setCourseCodes([]);
        }
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [entityQuery, sortBy, user?.id, type, doctor?.name]);

  useEffect(() => {
    if (!reviews || reviews.length === 0) return;

    const allTags = [];
    reviews.forEach((rev) => {
      const potentialSources = [
        rev.tags,
        rev.explanationtags,
        rev.attendancetags,
        rev.testtags,
        rev.gradetags,
        rev.othertags,
        rev.explanation_tags,
        rev.attendance_tags,
        rev.test_tags,
        rev.grade_tags,
        rev.other_tags,
      ];

      potentialSources.forEach((arr) => {
        if (Array.isArray(arr)) {
          arr.forEach((t) => {
            const txt = getTagText(t);
            if (txt) allTags.push(txt);
          });
        }
      });
    });

    const counts = {};
    allTags.forEach((t) => {
      counts[t] = (counts[t] || 0) + 1;
    });

    const sortedTags = Object.keys(counts)
      .sort((a, b) => counts[b] - counts[a])
      .slice(0, 15)
      .map((text) => ({ text, count: counts[text] }));

    if (sortedTags.length > 0) {
      setSummary((prev) => ({ ...prev, tags: sortedTags }));
    }
  }, [reviews]);

  useEffect(() => {
    const init = {};
    (summary.categories || []).forEach((_, i) => (init[i] = 0));
    setAnimatedPercentages(init);
    const delays = [300, 600, 900, 1200];
    (summary.categories || []).forEach((c, i) => {
      setTimeout(() => setAnimatedPercentages((p) => ({ ...p, [i]: toPct(c.percentage || 0) })), delays[i] || 0);
    });
  }, [summary.categories]);

  useEffect(() => {
    const init = {};
    reviews.forEach((ev, ei) =>
      (ev.categories || []).forEach((_, ci) => (init[`${ei}-${ci}`] = 0))
    );
    setReviewAnimatedPercentages(init);
    let d = 800;
    reviews.forEach((ev, ei) => {
      (ev.categories || []).forEach((c, ci) => {
        const key = `${ei}-${ci}`;
        setTimeout(() => setReviewAnimatedPercentages((p) => ({ ...p, [key]: toPct(c.percentage || 0) })), d);
        d += 80;
      });
    });
  }, [reviews]);

  useEffect(() => {
    if (!reviewId || !reviews.length) return;
    const t = setTimeout(() => {
      const el = Array.from(document.querySelectorAll("[data-review-id]"))
        .find((element) => element.dataset.reviewId === String(reviewId));
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.border = "2px solid #28E3DA";
        el.style.borderRadius = "8px";
        setTimeout(() => {
          el.style.border = "";
          el.style.borderRadius = "";
        }, 3000);
      }
    }, 120);
    return () => clearTimeout(t);
  }, [reviewId, reviews]);

  const [engaging, setEngaging] = useState({});

  const sendEngagement = async (reviewId, choice) => {
    if (!reviewId || engaging[reviewId]) return;

    if (!isAuthenticated || !user?.id) {
      openHeaderLoginModal();
      return;
    }

    const oldChoice = myChoices[reviewId] || null;
    const isToggleOff = oldChoice === choice;
    const nextChoice = isToggleOff ? null : choice;

    setEngaging((p) => ({ ...p, [reviewId]: true }));

    setReviews((list) =>
      list.map((ev) => {
        if ((ev.id || "").toString() !== (reviewId || "").toString()) return ev;
        const base = ev.engagement || { agree: 0, neutral: 0, disagree: 0 };
        const next = { ...base };

        if (oldChoice) next[oldChoice] = Math.max(0, (next[oldChoice] || 0) - 1);
        if (nextChoice) next[nextChoice] = (next[nextChoice] || 0) + 1;

        return { ...ev, engagement: next };
      })
    );

    setMyChoices((m) => {
      const cp = { ...m };
      if (nextChoice) cp[reviewId] = nextChoice;
      else delete cp[reviewId];
      return cp;
    });

    try {
      const r = await fetch(`${API_BASE}/reviews/engage`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(user?.id ? { "x-user-id": user.id } : {}),
        },
        body: JSON.stringify({ reviewId, choice: nextChoice }),
        credentials: "include",
      });
      if (!r.ok) throw new Error("engage failed");
      const data = await r.json();
      if (data && data.counts) {
        setReviews((list) =>
          list.map((ev) =>
            (ev.id || "").toString() !== (reviewId || "").toString()
              ? ev
              : {
                  ...ev,
                  engagement: {
                    agree: data.counts.agree || 0,
                    neutral: data.counts.neutral || 0,
                    disagree: data.counts.disagree || 0,
                  },
                }
          )
        );
      }
    } catch {
      setReviews((list) =>
        list.map((ev) => {
          if ((ev.id || "").toString() !== (reviewId || "").toString()) return ev;
          const base = ev.engagement || { agree: 0, neutral: 0, disagree: 0 };
          const undo = { ...base };

          if (nextChoice) undo[nextChoice] = Math.max(0, (undo[nextChoice] || 1) - 1);
          if (oldChoice) undo[oldChoice] = (undo[oldChoice] || 0) + 1;

          return { ...ev, engagement: undo };
        })
      );
      setMyChoices((m) => {
        const cp = { ...m };
        if (oldChoice) cp[reviewId] = oldChoice;
        else delete cp[reviewId];
        return cp;
      });
    } finally {
      setEngaging((p) => ({ ...p, [reviewId]: false }));
    }
  };

  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (!directoryLoading && !summaryLoading && !reviewsLoading) setInitialLoading(false);
  }, [directoryLoading, summaryLoading, reviewsLoading]);

  const hasAnyData = (summary?.categories?.length || 0) > 0 || (reviews?.length || 0) > 0;

  if (initialLoading) return <LoadingScreen />;

  const pageTitle = type === "course" ? "تقييم المقرر" : "تقييم المحاضر";
  const pageSubtitle = type === "course" ? "اطلع على المقرر وتجارب الطلاب" : "اطلع على المحاضر وتجارب الطلاب";
  const handleBack = () => navigate(-1);
  const handleAddEvaluation = () => setIsEvaluationModalOpen(true);

  const emojiBtn = (label, emoji, count, active, onClick, disabled) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`text-center rounded-lg px-3 py-2 transition-all ${
        active ? "ring-2 ring-[#28E3DA]" : "hover:bg-[#ffffff10]"
      } ${!isAuthenticated ? "opacity-90" : ""}`}
      aria-label={label}
      title={!isAuthenticated ? "سجّل الدخول للتصويت" : label}
    >
      <div className="text-3xl mb-1">{emoji}</div>
      <div className="text-white text-sm font-medium">{label}</div>
      <div className="text-gray-400 text-xs">العدد: {count ?? 0}</div>
    </button>
  );

  const pillTag = (text, key, dotColor = "#FFFFFF", borderColor = "rgba(255, 255, 255, 0.7)") => (
    <span
      key={key}
      className="flex items-center gap-2 px-3 py-1 rounded-3xl text-white text-xs font-light whitespace-nowrap"
      style={{
        background:
          "linear-gradient(0deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.04)), radial-gradient(92.12% 184.55% at 50% -55%, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%), #081423",
        border: `1px solid ${borderColor}`,
        borderRadius: "24px",
        fontFamily: "Alexandria, sans-serif",
        fontSize: "10px",
        lineHeight: "24px",
        display: "flex",
        alignItems: "center",
        textAlign: "right",
      }}
    >
      <div
        className="w-2 h-2 rounded-full"
        style={{
          background: dotColor,
          boxShadow: "0px 3px 20px rgba(255, 255, 255, 0.7)",
          borderRadius: "10px",
        }}
      ></div>
      {text}
    </span>
  );

  return (
    <div className="min-h-screen text-white bg-transparent" dir="rtl">
      <div className="absolute top-0 left-0 right-0 z-50 bg-transparent border-b border-[#28e3da40] pt-0 md:pt-12 p-4 md:p-6 mt-12 md:mt-16">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="w-10"></div>
          <div className="text-center flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{pageTitle}</h1>
            <p className="text-gray-300 text-sm md:text-base">{pageSubtitle}</p>
          </div>
          <button
            onClick={handleBack}
            className="w-10 h-10 bg-[#1a2a3a] rounded-lg flex items-center justify-center text-white hover:bg-[#2a3a4a] transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8 pt-48 md:pt-48">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            <div className="bg-[#1a2a3a] rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">المعلومات</h3>

              {directoryLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="space-y-2">
                      <div className="h-3 w-24 bg-white/10 rounded-full animate-pulse" />
                      <div className="h-4 w-40 bg-white/5 rounded-full animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : doctor ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-1">
                      {type === "course" ? "اسم المقرر" : "اسم المحاضر"}
                    </label>
                    <p className="text-white font-medium">{doctor.name}</p>
                  </div>

                  {type === "lecturer" ? (
                    <>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">التخصص</label>
                        <p className="text-white font-medium">{doctor.specialization}</p>
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">الكلية</label>
                        <p className="text-white font-medium">{doctor.college}</p>
                      </div>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">القسم</label>
                        <p className="text-white font-medium">{doctor.Department}</p>
                      </div>
                      {doctor.email && (
                        <div>
                          <label className="block text-gray-400 text-sm mb-1">الإيميل</label>
                          <p className="text-white font-medium">
                            <a
                              href={`mailto:${doctor.email}`}
                              className="underline underline-offset-2 hover:opacity-80"
                            >
                              {doctor.email}
                            </a>
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-gray-400 text-sm mb-1">الكلية</label>
                        <p className="text-white font-medium">{doctor.college}</p>
                      </div>

                      <div>
                        <label className="block text-gray-400 text-sm mb-1">القسم</label>
                        <p className="text-white font-medium">{doctor.Department}</p>
                      </div>

                      <div>
                        <label className="block text-gray-400 text-sm mb-1">الدفعة</label>
                        <p className="text-white font-medium">
                          {(dbPlanNos && dbPlanNos.length > 0 ? dbPlanNos.join(" / ") : "") || "—"}
                        </p>
                      </div>

                      <div>
                        <label className="block text-gray-400 text-sm mb-1">المتطلب</label>
                        <p className="text-white font-medium">
                          {(dbPrereqNames && dbPrereqNames.length > 0 ? dbPrereqNames.join(" / ") : "") || "—"}
                        </p>
                      </div>

                      {type === "course" && dbCourseCodes.length > 0 && (
                        <div className="mt-4 w-full md:col-span-2">
                          <p className="text-gray-400 text-xs mb-1">رموز المقرر</p>
                          <div className="flex flex-wrap gap-2">
                            {dbCourseCodes.map((code) => pillTag(code, `db-course-${code}`))}
                          </div>
                        </div>
                      )}

                      {type === "course" && dbPrereqCodes.length > 0 && (
                        <div className="mt-4 w-full md:col-span-2">
                          <p className="text-gray-400 text-xs mb-1">رموز المتطلب</p>
                          <div className="flex flex-wrap gap-2">
                            {dbPrereqCodes.map((code) => pillTag(code, `db-pre-${code}`))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : hasAnyData ? (
                <p className="text-gray-400">
                  {type === "course" ? "جاري التعرّف على اسم المقرر…" : "جاري التعرّف على اسم المحاضر…"}
                </p>
              ) : (
                <p className="text-red-400">
                  {type === "course"
                    ? "لم يتم العثور على مقرر مطابق. مرر الاسم في ?name= أو افتح الرابط باسم المقرر."
                    : "لم يتم العثور على محاضر مطابق. مرر الاسم في ?name= أو افتح الرابط باسم المحاضر."}
                </p>
              )}
            </div>

            {type === "lecturer" && (
              <div className="bg-[#1a2a3a] rounded-xl p-6 mb-6">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <div
                      className="w-24 h-24 rounded-full border-4 flex items-center justify-center transition-all duration-300"
                      style={{
                        borderColor: getRatingColor(summary.overallRating || 0),
                        backgroundColor: withAlpha(getRatingColor(summary.overallRating || 0), 0.12),
                      }}
                    >
                      {summaryLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-6 h-6 rounded-full border-2 border-t-transparent border-[#28e3da] animate-spin" />
                        </div>
                      ) : (
                        <span className="text-2xl font-bold text-white">{toPct(summary.overallRating || 0) + "%"}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-4">تفاصيل الأداء</h3>
                    {summaryLoading ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full border-2 border-t-transparent border-[#28e3da] animate-spin" />
                          <div className="flex flex-col gap-2 w-full max-w-xs">
                            <div className="h-3 w-32 rounded-full bg-white/10 animate-pulse" />
                            <div className="h-3 w-24 rounded-full bg-white/5 animate-pulse" />
                          </div>
                        </div>

                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-16 h-3 rounded-full bg-white/10 animate-pulse" />
                            <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                              <div className="h-2 w-1/2 rounded-full bg-white/20 animate-pulse" />
                            </div>
                            <div className="w-10 h-3 rounded-full bg-white/10 animate-pulse" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {(summary.categories || []).map((category, index) => {
                          const shown = toPct(animatedPercentages[index] || 0);
                          const categoryColor = getRatingColor(shown);
                          return (
                            <div key={index} className="flex items-center gap-3">
                              <span className="text-white text-sm w-16">{category.name}</span>
                              <div className="flex-1 bg-gray-700 rounded-full h-2">
                                <div
                                  className="h-2 rounded-full transition-all duration-1000 ease-out"
                                  style={{ width: `${shown}%`, backgroundColor: categoryColor }}
                                ></div>
                              </div>
                              <span className="text-white text-sm w-12">{shown}%</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="bg-[#1a2a3a] rounded-xl p-6 mb-6">
              <h3 className="text-lg font-semibold text-white mb-4">التصنيف</h3>
              {summaryLoading ? (
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <span
                      key={i}
                      className="px-4 py-2 rounded-3xl bg-white/5 border border-white/10 animate-pulse"
                      style={{ width: "72px", height: "24px" }}
                    ></span>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(summary.tags || []).map((tag, i) => {
                    const tagText = getTagText(tag);
                    if (!tagText) return null;
                    const t = tagVisualFromNameOrSentiment(tagText, tag.sentiment);
                    return (
                      <span
                        key={i}
                        className={`flex items-center gap-2 px-3 py-1 rounded-3xl text-white text-xs font-light ${
                          t.active ? "" : "opacity-60"
                        }`}
                        style={{
                          background: t.background,
                          border: `1px solid ${t.border}`,
                          borderRadius: "24px",
                          fontFamily: "Alexandria, sans-serif",
                          fontSize: "10px",
                          lineHeight: "24px",
                          display: "flex",
                          alignItems: "center",
                          textAlign: "right",
                        }}
                        title={t.active ? "" : "غير مفعّل: الاسم غير مطابق لقوائم التصنيف"}
                      >
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{
                            background: t.dot,
                            boxShadow: `0px 3px 20px ${t.shadow}`,
                            borderRadius: "10px",
                          }}
                        ></div>
                        {tagText}
                      </span>
                    );
                  })}
                  {(!summary.tags || summary.tags.length === 0) && <p className="text-gray-400">لا توجد وسوم بعد.</p>}
                </div>
              )}
            </div>

            <div className="bg-[#1a2a3a] rounded-xl p-6 h-fit">
              <h3 className="text-lg font-semibold text-white mb-4">الإحصائيات</h3>
              {summaryLoading ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#28e3da20] rounded-lg flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full border-2 border-t-transparent border-[#28e3da] animate-spin" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="h-4 w-16 bg-white/10 rounded-full animate-pulse" />
                      <div className="h-3 w-24 bg-white/5 rounded-full animate-pulse" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#28e3da20] rounded-lg flex items-center justify-center">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-[#28e3da]"
                      >
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-medium text-lg">{reviews.length}</p>
                      <p className="text-gray-400 text-sm">عدد التقييمات</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="text-center mb-8">
          {isAuthenticated ? (
            <button
              onClick={handleAddEvaluation}
              className="flex items-center justify-center gap-2 mx-auto transition-all duration-200"
              style={{
                width: "207px",
                height: "44px",
                borderRadius: "12px",
                background:
                  "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)",
                boxShadow:
                  "inset 0px -1px 0px rgba(255, 255, 255, 0.25), inset 0px 1px 0px rgba(255, 255, 255, 0.25)",
                fontFamily: "Alexandria, sans-serif",
                fontSize: "18px",
                fontWeight: "800",
                lineHeight: "100%",
                color: "#FFFFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-white">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              <span>أضف تقييماً</span>
            </button>
          ) : (
            <button
              onClick={openHeaderLoginModal}
              className="flex items-center justify-center gap-2 mx-auto transition-all duration-200 border border-[#EEEEEE] rounded-xl"
              style={{
                width: "260px",
                height: "44px",
                borderRadius: "12px",
                fontFamily: "Alexandria, sans-serif",
                fontSize: "16px",
                fontWeight: "700",
                lineHeight: "100%",
                color: "#EEEEEE",
                background: "transparent",
              }}
              aria-label="سجّل دخولك عشان التقييم"
              title="سجّل دخولك عشان التقييم"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h4a2 2 0 012 2v4" />
                <path d="M10 14L21 3" />
                <path d="M21 10v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9" />
              </svg>
              <span>سجّل دخولك عشان التقييم</span>
            </button>
          )}
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <span className="text-white font-medium text-lg">ترتيب حسب</span>
            <div className="flex gap-3">
              <button
                onClick={() => setSortBy("mostLiked")}
                className={`transition-all duration-200 border border-[#EEEEEE] rounded-xl ${
                  sortBy === "mostLiked" ? "text-[#EEEEEE]" : "bg-transparent text-[#EEEEEE]"
                }`}
                style={{
                  width: "128px",
                  height: "44px",
                  borderRadius: "12px",
                  fontFamily: "Alexandria, sans-serif",
                  fontSize: "18px",
                  fontWeight: "800",
                  lineHeight: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  background:
                    sortBy === "mostLiked"
                      ? "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)"
                      : "transparent",
                }}
              >
                الأكثر إعجابا
              </button>
              <button
                onClick={() => setSortBy("newest")}
                className={`transition-all duration-200 border border-[#EEEEEE] rounded-xl ${
                  sortBy === "newest" ? "text-[#EEEEEE]" : "bg-transparent text-[#EEEEEE]"
                }`}
                style={{
                  width: "128px",
                  height: "44px",
                  borderRadius: "12px",
                  fontFamily: "Alexandria, sans-serif",
                  fontSize: "18px",
                  fontWeight: "800",
                  lineHeight: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  background:
                    sortBy === "newest"
                      ? "radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)"
                      : "transparent",
                }}
              >
                الأحدث
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {reviewsLoading ? (
            <>
              {[1, 2].map((i) => (
                <div
                  key={i}
                  className="bg-[#0f1e2e] rounded-xl p-4 md:p-6 border border-[#28e3da20] animate-pulse space-y-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-6 bg-white/10 rounded-full" />
                      <div className="w-40 h-4 bg-white/5 rounded-full" />
                    </div>
                    <div className="w-24 h-4 bg-white/10 rounded-full" />
                  </div>
                  <div className="w-full h-16 bg-white/5 rounded-lg" />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[1, 2, 3, 4].map((j) => (
                      <div key={j} className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
                          <div className="h-2 w-1/2 rounded-full bg-white/20" />
                        </div>
                        <div className="w-16 h-3 bg-white/10 rounded-full" />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center gap-4">
                    {[1, 2, 3].map((j) => (
                      <div key={j} className="w-20 h-14 bg-white/5 rounded-lg" />
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : reviews.length === 0 ? (
            <p className="text-gray-400">لا توجد تقييمات بعد.</p>
          ) : (
            reviews.map((evaluation, evalIdx) => {
              const key = evaluation.id || `${doctor?.name || "doc"}-${evaluation.timestamp || "t"}-${evalIdx}`;
              const my = myChoices[evaluation.id];
              const busy = !!engaging[evaluation.id];

              const reviewCourseCodes = type === "course" ? getReviewCourseCodes(evaluation) : [];
              const mainCourseCode = type === "course" ? reviewCourseCodes[0] || "" : "";

              const lecturerName =
                evaluation.lecturer ||
                evaluation.doctorName ||
                evaluation.doctor ||
                evaluation.teacher ||
                "";

              return (
                <div
                  key={key}
                  className="bg-[#0f1e2e] rounded-xl p-4 md:p-6 border border-[#28e3da20]"
                  data-review-id={String(evaluation.id || key)}
                  data-user-review={evaluation.isUserReview ? "true" : undefined}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4 flex-wrap md:flex-nowrap">
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-gray-400 text-sm whitespace-nowrap">الدرجة:</span>
                        <span
                          className="inline-flex items-center justify-center font-bold text-white px-4 py-1.5 rounded-[12px] text-[14px] md:text-[16px] whitespace-nowrap shrink-0"
                          style={{ color: "#D5D2D2", ...getClassificationColors(evaluation.classification) }}
                        >
                          {evaluation.classification || "—"}
                        </span>

                        {type === "course" && mainCourseCode && pillTag(mainCourseCode, `rev-code-${key}-${mainCourseCode}`)}
                      </div>

                      {type === "lecturer" && evaluation.course && (
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-gray-400 text-sm shrink-0 whitespace-nowrap">المقرر:</span>
                          <span className="text-white font-medium truncate max-w-[40ch]">{evaluation.course}</span>
                        </div>
                      )}

                      {type === "course" && lecturerName && (
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-gray-400 text-sm shrink-0 whitespace-nowrap">المحاضر:</span>
                          <span className="text-white font-medium truncate max-w-[40ch]">{lecturerName}</span>
                        </div>
                      )}
                    </div>

                    <span className="text-gray-400 text-sm whitespace-nowrap">{evaluation.timestamp}</span>
                  </div>

                  <div className="mb-4">
                    <div className="bg-[#1a2a3a] rounded-lg p-4 min-h-[80px] flex items-center">
                      <span className="text-gray-400">{evaluation.comment || "—"}</span>
                    </div>
                  </div>

                  {type === "lecturer" && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {(evaluation.categories || []).map((category, index) => {
                        const reviewKey = `${evalIdx}-${index}`;
                        const shown = toPct(reviewAnimatedPercentages[reviewKey] || 0);
                        const categoryColor = getRatingColor(shown);
                        return (
                          <div key={index} className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-700 rounded-full h-2">
                              <div
                                className="h-2 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${shown}%`, backgroundColor: categoryColor }}
                              ></div>
                            </div>
                            <span className="text-white text-sm font-medium">
                              {shown}% {category.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex items-center justify-center gap-6 mb-4">
                    {emojiBtn("متفق", "😊", evaluation.engagement?.agree ?? 0, my === "agree", () => sendEngagement(evaluation.id, "agree"), busy)}
                    {emojiBtn("محايد", "😐", evaluation.engagement?.neutral ?? 0, my === "neutral", () => sendEngagement(evaluation.id, "neutral"), busy)}
                    {emojiBtn("معترض", "😞", evaluation.engagement?.disagree ?? 0, my === "disagree", () => sendEngagement(evaluation.id, "disagree"), busy)}
                  </div>

                  <div className="flex flex-wrap gap-2 justify-center">
                    {(evaluation.tags || []).map((tag, index) => {
                      const tagText = getTagText(tag);
                      if (!tagText) return null;
                      const t = tagVisualFromNameOrSentiment(tagText, tag.sentiment);
                      return (
                        <span
                          key={index}
                          className={`flex items-center gap-2 px-3 py-1 rounded-3xl text-white text-xs font-light ${
                            t.active ? "" : "opacity-60"
                          }`}
                          style={{
                            background: t.background,
                            border: `1px solid ${t.border}`,
                            borderRadius: "24px",
                            fontFamily: "Alexandria, sans-serif",
                            fontSize: "10px",
                            lineHeight: "24px",
                            display: "flex",
                            alignItems: "center",
                            textAlign: "right",
                          }}
                          title={t.active ? "" : "غير مفعّل: الاسم غير مطابق لقوائم التصنيف"}
                        >
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{
                              background: t.dot,
                              boxShadow: `0px 3px 20px ${t.shadow}`,
                              borderRadius: "10px",
                            }}
                          ></div>
                          {tagText}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {isEvaluationModalOpen && (entityQuery || doctor?.name) && (
        <EvaluationModal
          isOpen={isEvaluationModalOpen}
          onClose={() => setIsEvaluationModalOpen(false)}
          lecturerName={doctor?.name || guessedName || ""}
          type={type}
        />
      )}
    </div>
  );
};

export default EvaluationDetailsPage;
